import HighlightedPips from '@/components/common/HighlightedPips';
import InstrumentInfo from '@/components/common/InstrumentInfo';
import { tickerIdleTimer } from '@/config/config';
import { isDebugInstance } from '@/helpers/environmentHelper';
import { Modify } from '@/model/common';
import { switchOrderBook } from '@/services/MarketService';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import {
    PairMap,
    addFavouritePair,
    removeFavouritePair,
    selectFavouriteMarketPairs
} from '@/state/reducers/marketPairSlice';
import { GetColorIndexesReturn, GetPipIndexesReturn } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { useLatencyIdle } from '@/utils/hooks/useLatencyIdle';
import useTicker from '@/utils/hooks/useTicker';
import { useTickerIdle } from '@/utils/hooks/useTickerIdle';
import cn from 'classnames';
import { CSSProperties, Fragment, useMemo } from 'react';
import { MdStar, MdStarOutline } from 'react-icons/md';

export interface UserMarketProps {
    market: PairMap;
    style: CSSProperties;
    isActive?: boolean;
    onClick?: () => void;
    onActive?: () => void;
}

const UserMarket = (props: UserMarketProps) => {
    const { market, style, isActive, onClick, onActive } = props;

    return (
        <Fragment>
            {market.celer === 'header' ? (
                <MarketListHeader style={style} />
            ) : (
                <div
                    style={style}
                    onClick={() => {
                        switchOrderBook(market);
                        onClick && onClick();
                    }}>
                    <div
                        className={cn(
                            'cursor-pointer flex text-xs py-1.5 px-2 text-brand-text justify-between hover:bg-brand-neutral-700',
                            { 'bg-brand-background-dark': isActive }
                        )}
                        onClick={onActive}>
                        <MarketInstrument {...props} />
                        <BidAsks {...props} />
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default UserMarket;

type MarketInstrumentProps = Modify<
    Omit<UserMarketProps, 'style'>,
    {
        isMobileSubHeader?: boolean;
    }
>;

export const MarketInstrument = ({ market, isMobileSubHeader }: MarketInstrumentProps) => {
    const [base, quote] = market.show.split('/');

    return (
        <div className="min-w-28 flex items-center">
            <InstrumentInfo
                market={market}
                offsetOverride={{
                    mainAxis: 4,
                    crossAxis: 4
                }}
            />
            <div className="flex items-center leading-none">
                <span className="">{base}</span>
                <span className="text-brand-neutral-400">/{quote}</span>
            </div>
        </div>
    );
};

interface MarketListHeaderProps {
    ref?: React.Ref<HTMLDivElement>;
    style: CSSProperties;
}

export const MarketListHeader = ({ ref, style }: MarketListHeaderProps) => {
    const lang = useLanguage();
    return (
        <div ref={ref} style={style} className="flex text-brand-neutral-200 text-sm px-2 justify-between">
            <div className="min-w-28">{lang.blotters.instrument}</div>
            <div className="flex gap-2">
                <div className="min-w-20 flex justify-end">{lang.orderBook.bid}</div>
                <div className="min-w-12 flex items-center justify-center px-1">
                    {/* <div className="hidden 2xl:block">Spread</div> */}
                </div>
                <div className="min-w-20 flex justify-start">{lang.orderBook.ask}</div>
                {isDebugInstance && <div className="min-w-20 flex-1 flex justify-end">{lang.common.latencies}</div>}
                <div className="min-w-4 flex items-center justify-center px-1">
                    {/* <div className="hidden 2xl:block">Spread</div> */}
                </div>
            </div>
        </div>
    );
};

export const BidAsks = ({ market }: UserMarketProps) => {
    const { formattedBid, formattedAsk, spread, bidHighlights, askHighlights, latency } = useTicker(market);
    const dispatch = useAppDispatch();
    const userFavouriteMarkets = useAppSelector(selectFavouriteMarketPairs);

    const isFavourite = useMemo(() => {
        return userFavouriteMarkets.map((market) => market.celer).includes(market.celer);
    }, [market, userFavouriteMarkets]);
    return (
        <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
                <div className="min-w-20 flex items-center justify-end">
                    {formattedBid && <Highlights ticker={formattedBid} highlights={bidHighlights} />}
                </div>
                <div className="min-w-12 flex items-center justify-center px-1 leading-none text-brand-neutral-400">
                    {spread || '-'}
                </div>
                <div className="min-w-20 flex items-center justify-start">
                    {formattedAsk && <Highlights ticker={formattedAsk} highlights={askHighlights} />}
                </div>
                {isDebugInstance && (
                    <div className="min-w-20 flex-1 flex items-center justify-end">
                        <Latencies latency={latency} />
                    </div>
                )}
            </div>
            <div
                className="flex items-center cursor-pointer"
                onClick={(e) => {
                    if (isFavourite) dispatch(removeFavouritePair(market));
                    else dispatch(addFavouritePair(market));

                    e.stopPropagation();
                }}>
                {isFavourite ? (
                    <MdStar className="h-4 w-4 fill-yellow-400 hover:fill-yellow-300" />
                ) : (
                    <MdStarOutline className="h-4 w-4 fill-brand-neutral-100 hover:fill-yellow-300" />
                )}
            </div>
        </div>
    );
};

interface HighlightsProps {
    ticker: GetPipIndexesReturn;
    highlights?: GetColorIndexesReturn;
}

export const Highlights = ({ ticker, highlights }: HighlightsProps) => {
    // check if either bid or ask in this ticker is stale for more than 5 seconds.
    // if stale for more than 5 seconds, ticker is idle. if idle, use default colour
    const tickerIdle = useTickerIdle(tickerIdleTimer, highlights);
    return (
        <HighlightedPips
            price={ticker.price}
            boldIndexes={ticker.pipIndexes}
            colorIndexes={!tickerIdle ? highlights?.colorIndexes : []}
            color={!tickerIdle ? highlights?.color : 'text-brand-text'}
        />
    );
};

interface LatenciesProps {
    latency: {
        timestamp: number;
        value: string;
    };
}

export const Latencies = ({ latency }: LatenciesProps) => {
    const tickerIdle = useLatencyIdle(tickerIdleTimer, latency, false);
    return <div>{tickerIdle ? '' : latency.value}</div>;
};
